import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { Button } from './ui/button';
import apiClient from '../services/api';

const CATEGORY_COLOR = {
  info: 'bg-slate-100 text-slate-700',
  shift: 'bg-cyan-50 text-cyan-700',
  alert: 'bg-red-50 text-red-700',
  security: 'bg-amber-50 text-amber-700',
};

export const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/notifications/unread-count');
      setUnread(data.unread || 0);
    } catch {
      // ignore
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/notifications', { params: { limit: 30 } });
      setItems(data.notifications || []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const markRead = async (id) => {
    try {
      await apiClient.post(`/notifications/${id}/read`);
      setItems((p) => p.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((n) => Math.max(0, n - 1));
    } catch {
      // ignore
    }
  };

  const markAll = async () => {
    try {
      await apiClient.post('/notifications/read-all');
      setItems((p) => p.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative"
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-btn"
      >
        <Bell className="w-4 h-4 text-slate-500" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center font-medium"
            data-testid="notif-unread-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 md:w-96 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden"
          data-testid="notif-panel"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50">
            <span className="text-sm font-semibold text-slate-800">Notifications</span>
            <button
              onClick={markAll}
              className="text-xs text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1"
              data-testid="notif-read-all"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Tout marquer lu
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">Aucune notification</div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${
                    !n.read ? 'bg-cyan-50/40' : ''
                  }`}
                  onClick={() => !n.read && markRead(n.id)}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full ${
                            CATEGORY_COLOR[n.category] || CATEGORY_COLOR.info
                          }`}
                        >
                          {n.category}
                        </span>
                        <span className="text-sm font-medium text-slate-900 truncate">{n.title}</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {new Date(n.createdAt).toLocaleString('fr-FR')}
                      </p>
                    </div>
                    {!n.read && <Check className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
